import { zhEn } from "./posTier.js";
// 優惠 engine — pure, deterministic, no React and no imports, so it runs in
// plain node for tests. Same rules as the shop's old PetPOS engine:
//   • only product lines; a line the cashier touched (改價 / 折扣 % / manual
//     discount / skipPromos) is never touched
//   • no stacking: every line belongs to at most ONE promotion — its discount
//     and (for 可混款 groups) its units count only towards that promotion
//   • 獨享 (exclusive) promotions claim lines before any non-exclusive one
//   • a promo discount travels as line.discount + line.promoId/promoName;
//     checkout_sale accepts it from staff without the discount permission as
//     long as the promotion is active, and counts max_uses once per sale.
//
// How lines are shared out (greedy, deterministic):
//   pass 1 = exclusive promotions, pass 2 = the rest. In each pass, repeatedly
//   take the single option worth the most (one line for a per-line promo, or a
//   whole 可混款 group computed over the still-free lines only), give it its
//   lines, and recompute the rest. A 買X送Y group claims the lines whose units
//   it uses (paid + free); leftover lines stay free for other promotions.
//   Ties → sort_order, then id, then line order.
//
// Minimum spend: the NET amount the customer pays — lines this promotion
// would discount count at full price, every other line at its price after its
// own (manual or other-promotion) discount. If a chosen promotion then falls
// short, it is dropped and the share-out is redone.
//
// Extensions on top of the base params (server trusts the client's discount,
// so these are safe):
//   • bogo params.percent (default 100) = how much the "free" units get off,
//     so 第二件半價 = bogo {buyQty 1, freeQty 1, percent 50}. Free units are
//     always the cheapest units; their value goes on the lines they belong to.
//   • amount + params.mixGroup = ONE flat amount split across the matching
//     lines by value (滿 $300 減 $30), instead of a flat amount per line.
// Rows that don't make sense (written outside the UI: NaN, buyQty < 1, …)
// are ignored.

export const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

// Local calendar date YYYY-MM-DD (the shop's day, not UTC).
const toDate = (d) => (d instanceof Date ? d : new Date(d ?? Date.now()));
export const localDate = (d = new Date()) => {
  const x = toDate(d);
  return x.getFullYear() + "-" + String(x.getMonth() + 1).padStart(2, "0") + "-" + String(x.getDate()).padStart(2, "0");
};

// Same as posMath.effectivePrice, inlined so this file has no imports.
const saleActive = (p, ds) => p?.salePrice != null && p.salePrice !== "" &&
  (!p.saleFrom || p.saleFrom <= ds) && (!p.saleTo || p.saleTo >= ds);
export const isOnSale = (p, ds = localDate()) => !!p && saleActive(p, ds) && (Number(p.salePrice) || 0) < (Number(p.price) || 0);

const num = (v, d = 0) => { const n = Number(v); return Number.isFinite(n) ? n : d; };
const has = (v) => v != null && v !== "";
const fin = (v) => has(v) && Number.isFinite(Number(v));
const gross = (l) => round2(num(l.qty) * num(l.unitPrice));

// A discount the cashier set by hand. A discount without a promoId is manual
// too (old tickets, held tickets from before promotions existed).
export const isManualLine = (l) => !!(l.override || l.manualDiscount || l.skipPromos ||
  num(l.discountPct) > 0 || (num(l.discount) > 0 && !l.promoId));

export const promoLabel = (p) => (p?.display_name || p?.name || zhEn("優惠", "Promotion")).trim();

// Percent off → 折 label: 10 → "9 折", 15 → "85 折", 12.5 → "87.5 折",
// 33.333 → "66.67 折", 95 → "0.5 折", 50 → "半價", 100 → "免費".
export const offLabel = (pct) => {
  const p = num(pct);
  if (p >= 100) return zhEn("免費", "free");
  if (p === 50) return zhEn("半價", "half price");
  const z = round2(100 - p);           // % the customer still pays
  if (z <= 0) return zhEn("免費", "free");
  const v = round2(z / 10);
  const s = Number.isInteger(v) || z < 10 ? String(v) : String(z);   // String() drops trailing zeros
  return zhEn(s + " 折", p + "% off");
};

const sortPromos = (a, b) => num(a.sort_order) - num(b.sort_order) || String(a.id).localeCompare(String(b.id));
const params = (promo) => (promo?.params && typeof promo.params === "object" ? promo.params : {});
const pctOk = (v) => fin(v) && Number(v) > 0 && Number(v) <= 100;
const intAtLeast1 = (v) => fin(v) && Math.floor(Number(v)) >= 1;

// Params make sense for the type (rows written outside the UI may not).
export function promoParamsValid(promo) {
  const pr = params(promo);
  switch (promo?.type) {
    case "percent": return pctOk(pr.percent);
    case "amount": return fin(pr.amount) && Number(pr.amount) > 0;
    case "fixed_price": return fin(pr.price) && Number(pr.price) >= 0;
    case "bogo": return intAtLeast1(pr.buyQty) && intAtLeast1(pr.freeQty) && (!has(pr.percent) || pctOk(pr.percent));
    case "bundle": return intAtLeast1(pr.buyQty) && pctOk(pr.percent);
    case "tiered": return Array.isArray(pr.tiers) && pr.tiers.length > 0 &&
      pr.tiers.every(t => t && intAtLeast1(t.min) && (has(t.percent) ? pctOk(t.percent) : fin(t.amount) && Number(t.amount) > 0));
    default: return false;
  }
}

// Status / dates / weekdays / usage limit only (no code, no min spend).
function liveBlocker(promo, ds, dow) {
  if (!promo || promo.status !== "active") return zhEn("已停用", "Disabled");
  if (promo.starts_on && ds < String(promo.starts_on).slice(0, 10)) return zhEn("未開始", "Not started");
  if (promo.ends_on && ds > String(promo.ends_on).slice(0, 10)) return zhEn("已過期", "Expired");
  const days = Array.isArray(promo.days_of_week) ? promo.days_of_week.map(Number) : [];
  if (days.length && !days.includes(dow)) return zhEn("今日不適用", "Not valid today");
  if (has(promo.max_uses) && num(promo.used_count) >= num(promo.max_uses)) return zhEn("已用完", "Used up");
  return "";
}
// Same test the register uses: active, inside its dates, on today's weekday,
// not used up. Code and minimum spend depend on the cart, so not checked here.
export const promoIsLive = (promo, today = new Date()) => !liveBlocker(promo, localDate(today), toDate(today).getDay());

// Why a promotion doesn't apply. "" when it qualifies. `subtotal` = the
// amount min_spend is compared with.
export function promoBlocker(promo, { ds, dow, codes = [], subtotal = 0 }) {
  const live = liveBlocker(promo, ds, dow);
  if (live) return live;
  if (!promoParamsValid(promo)) return zhEn("設定有誤", "Settings are invalid");
  if (promo.code_required) {
    const want = String(promo.code || "").trim().toUpperCase();
    if (!want || !codes.some(c => String(c || "").trim().toUpperCase() === want)) return zhEn("需要優惠碼", "A promo code is needed");
  }
  if (num(promo.min_spend) > 0 && subtotal + 0.0001 < num(promo.min_spend)) return zhEn("未夠消費", "Spend more to qualify");
  return "";
}

const inIds = (ids, v) => v != null && v !== "" && ids.includes(String(v));
export function promoMatchesProduct(promo, p) {
  if (!p) return false;
  const ids = (promo.target_ids || []).map(String);
  switch (promo.target || "all") {
    case "all": return true;
    case "category": return inIds(ids, p.categoryId) || inIds(ids, p.category) || (p.categories || []).some(c => inIds(ids, c?.id ?? c) || inIds(ids, c?.name));
    case "brand": return inIds(ids, p.brandId) || inIds(ids, p.brand);
    case "product": return inIds(ids, p.id) || inIds(ids, p.parentId);
    case "tag": return (p.tags || []).some(t => inIds(ids, t));
    default: return false;
  }
}

const bestTier = (tiers, qty) => (Array.isArray(tiers) ? tiers : [])
  .filter(t => num(t.min) > 0 && num(t.min) <= qty)
  .sort((a, b) => num(b.min) - num(a.min))[0] || null;
const bogoPct = (pr) => (has(pr.percent) ? num(pr.percent) : 100);
const clampDisc = (d, sub) => Math.max(0, Math.min(round2(d), sub));

// Split `total` across entries by subtotal share; cents remainder on the largest.
function splitByShare(total, items) {
  const S = items.reduce((s, x) => s + x.sub, 0);
  if (!(S > 0) || !(total > 0)) return items.map(() => 0);
  const out = items.map(x => round2(total * x.sub / S));
  let big = 0;
  items.forEach((x, i) => { if (x.sub > items[big].sub) big = i; });
  out[big] = round2(out[big] + round2(total - out.reduce((s, v) => s + v, 0)));
  return out.map((v, i) => clampDisc(v, items[i].sub));
}

const isMix = (promo) => !!params(promo).mixGroup && ["bogo", "bundle", "tiered", "amount"].includes(promo.type);

// One promotion over ONE line (not 可混款). → discount
function lineDiscount(promo, e) {
  const pr = params(promo);
  const { qty, sub, unit } = e;
  let d = 0;
  switch (promo.type) {
    case "percent": d = sub * num(pr.percent) / 100; break;
    case "amount": d = Math.min(num(pr.amount), sub); break;
    case "fixed_price": d = Math.max(0, sub - num(pr.price) * qty); break;
    case "bogo": {
      const buy = Math.floor(num(pr.buyQty)), free = Math.floor(num(pr.freeQty));
      d = Math.floor(qty / (buy + free)) * free * unit * bogoPct(pr) / 100; break;
    }
    case "bundle": d = qty >= Math.floor(num(pr.buyQty)) ? sub * num(pr.percent) / 100 : 0; break;
    case "tiered": {
      const t = bestTier(pr.tiers, qty);
      d = !t ? 0 : has(t.percent) ? sub * num(t.percent) / 100 : Math.min(num(t.amount), sub); break;
    }
    default: d = 0;
  }
  return clampDisc(d, sub);
}

// One 可混款 promotion over the given (free) entries.
// → { alloc: Map(lineIndex → discount), claim: [lineIndex] } or null
function groupOption(promo, ents) {
  const pr = params(promo);
  const Q = ents.reduce((s, e) => s + e.qty, 0);
  const alloc = new Map();
  if (promo.type === "bogo") {
    const buy = Math.floor(num(pr.buyQty)), free = Math.floor(num(pr.freeQty));
    const sets = Math.floor(Q / (buy + free));
    if (!sets) return null;
    const units = [];
    for (const e of ents) for (let k = 0; k < Math.floor(e.qty); k++) units.push({ i: e.i, price: e.unit });
    const asc = [...units].sort((a, b) => a.price - b.price || a.i - b.i);
    const freeUnits = asc.slice(0, sets * free);                                  // cheapest go free
    const paidUnits = asc.slice(sets * free).sort((a, b) => b.price - a.price || a.i - b.i).slice(0, sets * buy);
    const raw = new Map();
    for (const u of freeUnits) raw.set(u.i, (raw.get(u.i) || 0) + u.price * bogoPct(pr) / 100);
    for (const e of ents) if (raw.has(e.i)) alloc.set(e.i, clampDisc(raw.get(e.i), e.sub));
    const claim = [...new Set([...freeUnits, ...paidUnits].map(u => u.i))];
    return { alloc, claim };
  }
  if (promo.type === "bundle") {
    if (Q < Math.floor(num(pr.buyQty))) return null;
    for (const e of ents) alloc.set(e.i, clampDisc(e.sub * num(pr.percent) / 100, e.sub));
  } else if (promo.type === "tiered") {
    const t = bestTier(pr.tiers, Q);
    if (!t) return null;
    if (has(t.percent)) for (const e of ents) alloc.set(e.i, clampDisc(e.sub * num(t.percent) / 100, e.sub));
    else {
      const S = round2(ents.reduce((s, e) => s + e.sub, 0));
      splitByShare(Math.min(round2(num(t.amount)), S), ents).forEach((d, k) => alloc.set(ents[k].i, d));
    }
  } else if (promo.type === "amount") {
    const S = round2(ents.reduce((s, e) => s + e.sub, 0));
    splitByShare(Math.min(round2(num(pr.amount)), S), ents).forEach((d, k) => alloc.set(ents[k].i, d));
  }
  return { alloc, claim: ents.map(e => e.i) };
}
const optTotal = (o) => round2([...o.alloc.values()].reduce((s, v) => s + v, 0));

// Greedy share-out (see header). → Map(lineIndex → {promo, d})
function shareOut(pool, elig, matchesOf) {
  const asg = new Map();
  for (const tier of [pool.filter(p => p.exclusive), pool.filter(p => !p.exclusive)]) {
    for (;;) {
      let best = null, bestTotal = 0;
      for (const promo of tier) {
        const ents = elig.filter(e => !asg.has(e.i) && matchesOf(promo, e));
        if (!ents.length) continue;
        const opts = isMix(promo) ? [groupOption(promo, ents)]
          : ents.map(e => { const d = lineDiscount(promo, e); return { alloc: new Map([[e.i, d]]), claim: [e.i] }; });
        for (const o of opts) {
          if (!o) continue;
          const t = optTotal(o);
          if (t > bestTotal + 0.000001) { best = { promo, o }; bestTotal = t; }
        }
      }
      if (!best) break;
      for (const i of best.o.claim) asg.set(i, { promo: best.promo, d: best.o.alloc.get(i) || 0 });
    }
  }
  return asg;
}

// Units still needed to reach the next threshold (null = none / not useful).
function nextStep(promo, qty) {
  const pr = params(promo);
  if (qty <= 0) return null;
  if (promo.type === "bundle") {
    const b = Math.floor(num(pr.buyQty));
    return qty < b ? { need: b - qty, text: offLabel(pr.percent) } : null;
  }
  if (promo.type === "bogo") {
    const buy = Math.floor(num(pr.buyQty)), free = Math.floor(num(pr.freeQty));
    const r = Math.floor(qty) % (buy + free);
    if (r === 0) return null;
    const need = buy + free - r;
    const pct = bogoPct(pr);
    return { need, text: pct >= 100 ? (need <= free ? zhEn("免費", "free") : zhEn("買 " + buy + " 送 " + free, "Buy " + buy + " get " + free + " free")) : (need <= free ? offLabel(pct) : bogoLabel(pr)) };
  }
  if (promo.type === "tiered") {
    const t = (Array.isArray(pr.tiers) ? pr.tiers : []).filter(x => num(x.min) > qty).sort((a, b) => num(a.min) - num(b.min))[0];
    if (!t) return null;
    return { need: num(t.min) - qty, text: has(t.percent) ? offLabel(t.percent) : zhEn("減 $", "$ off") + round2(num(t.amount)) };
  }
  return null;
}
const MAX_NUDGE = 3;

/**
 * applyPromotions — returns { lines, applied, nudges, codeStatus, changed }.
 * Lines that end up identical keep the SAME object, and `changed` is false
 * when nothing moved, so a caller can write back only when changed (no loop):
 * applyPromotions on its own output always gives changed === false.
 */
export function applyPromotions({ lines = [], products = [], promotions = [], codes = [], today = new Date(), customerId = null } = {}) {
  void customerId; // reserved (member-only promotions)
  const ds = localDate(today);
  const dow = toDate(today).getDay();
  const byId = new Map(products.map(p => [p.id, p]));
  const promos = [...promotions].filter(Boolean).sort(sortPromos);
  const retail = lines.map((l, i) => ({ l, i })).filter(x => x.l.kind === "product" || x.l.kind === "custom");

  // Eligible lines (promo discounts are recomputed from scratch every time).
  const elig = [];
  lines.forEach((l, i) => {
    if (l.kind !== "product" || isManualLine(l)) return;
    const p = byId.get(l.productId);
    const sub = gross(l);
    if (!p || num(l.qty) <= 0 || !(sub > 0)) return;
    elig.push({ i, l, p, sub, qty: num(l.qty), unit: num(l.unitPrice) });
  });
  const eligIdx = new Set(elig.map(e => e.i));
  const matchesOf = (promo, e) => promoMatchesProduct(promo, e.p) && !(promo.exclude_on_sale && isOnSale(e.p, ds));
  // Discount a non-eligible line keeps no matter what (manual ones).
  const fixedDisc = (l) => (isManualLine(l) ? Math.max(0, Math.min(num(l.discount), gross(l))) : 0);
  // Net spend for promo's min_spend, given a share-out.
  const spendFor = (promo, asg) => round2(retail.reduce((s, { l, i }) => {
    const g = gross(l);
    if (eligIdx.has(i)) {
      const e = elig.find(x => x.i === i);
      if (matchesOf(promo, e)) return s + g;
      return s + g - (asg?.get(i)?.d || 0);
    }
    return s + g - fixedDisc(l);
  }, 0));

  // Everything but min spend; min spend first against the best case (no other promos).
  const ctxNoMin = { ds, dow, codes, subtotal: Infinity };
  let pool = promos.filter(p => !promoBlocker(p, ctxNoMin) && !(num(p.min_spend) > 0 && spendFor(p, null) + 0.0001 < num(p.min_spend)));
  const shortOfSpend = new Set(promos.filter(p => !promoBlocker(p, ctxNoMin) && !pool.includes(p)).map(p => p.id));
  let asg = shareOut(pool, elig, matchesOf);
  for (let guard = 0; guard <= promos.length; guard++) {
    const chosen = pool.filter(p => [...asg.values()].some(a => a.promo === p));
    const failing = chosen.filter(p => num(p.min_spend) > 0 && spendFor(p, asg) + 0.0001 < num(p.min_spend));
    if (!failing.length) break;
    const drop = failing[failing.length - 1];
    shortOfSpend.add(drop.id);
    pool = pool.filter(p => p !== drop);
    asg = shareOut(pool, elig, matchesOf);
  }

  // Nudges — over the lines a promotion holds plus the still-free ones.
  const nudges = [];
  for (const promo of pool) {
    const ents = elig.filter(e => matchesOf(promo, e) && (!asg.has(e.i) || asg.get(e.i).promo === promo));
    if (!ents.length) continue;
    let st = null;
    if (isMix(promo)) st = nextStep(promo, ents.reduce((s, e) => s + e.qty, 0));
    else for (const e of ents) { const x = nextStep(promo, e.qty); if (x && (!st || x.need < st.need)) st = x; }
    if (st && st.need <= MAX_NUDGE) nudges.push({ promoId: promo.id, name: promoLabel(promo), need: st.need, text: zhEn("再買 " + st.need + " 件即享 " + st.text, "Add " + st.need + " more to get " + st.text) });
  }

  const applied = new Map();
  let changed = false;
  const out = lines.map((l, i) => {
    const b = asg.get(i);
    if (b) {
      const name = promoLabel(b.promo);
      const a = applied.get(b.promo.id) || { promoId: b.promo.id, name, discount: 0 };
      a.discount = round2(a.discount + b.d);
      applied.set(b.promo.id, a);
      if (num(l.discount) === b.d && l.promoId === b.promo.id && l.promoName === name) return l;
      changed = true;
      return { ...l, discount: b.d, promoId: b.promo.id, promoName: name };
    }
    if (l.promoId || l.promoName) {
      changed = true;
      const { promoId: _a, promoName: _b, ...rest } = l;
      // Promo-driven discount goes away; a manual one (cashier changed the
      // line after the promo applied) is kept, only the promo tag is dropped.
      return isManualLine(l) ? rest : { ...rest, discount: 0 };
    }
    return l;
  });

  // Entered codes → feedback for the 優惠碼 field.
  const seen = new Set();
  const codeStatus = [];
  for (const raw of codes) {
    const code = String(raw || "").trim().toUpperCase();
    if (!code || seen.has(code)) continue;
    seen.add(code);
    const promo = promos.find(p => String(p.code || "").trim().toUpperCase() === code);
    if (!promo) { codeStatus.push({ code, ok: false, promoId: null, text: zhEn("無效優惠碼", "Invalid promo code") }); continue; }
    const why = promoBlocker(promo, ctxNoMin);
    const base = { code, promoId: promo.id, name: promoLabel(promo) };
    if (why) codeStatus.push({ ...base, ok: false, text: why });
    else if (shortOfSpend.has(promo.id)) codeStatus.push({ ...base, ok: false, text: zhEn("滿 $" + round2(num(promo.min_spend)) + " 先用得", "Spend $" + round2(num(promo.min_spend)) + " to use it") });
    else if (!applied.has(promo.id)) codeStatus.push({ ...base, ok: false, text: zhEn("購物車冇適用商品", "No eligible items in the cart") });
    else codeStatus.push({ ...base, ok: true, text: promoLabel(promo) + " −$" + applied.get(promo.id).discount });
  }

  return { lines: changed ? out : lines, applied: [...applied.values()], nudges, codeStatus, changed };
}

// ── plain-language summary (like WooCommerce's coupon summary) ──────────────
const DOW = [zhEn("日", "Sun"), zhEn("一", "Mon"), zhEn("二", "Tue"), zhEn("三", "Wed"), zhEn("四", "Thu"), zhEn("五", "Fri"), zhEn("六", "Sat")];
const listNames = (names, unit) => {
  const ns = names.filter(Boolean);
  if (!ns.length) return zhEn("（未揀" + unit + "）", "(none chosen)");
  return ns.length <= 2 ? zhEn(ns.join("、"), ns.join(", ")) : zhEn(ns.slice(0, 2).join("、") + " 等 " + ns.length + " " + unit, ns.slice(0, 2).join(", ") + " +" + (ns.length - 2) + " more");
};
const md = (d) => { const s = String(d).slice(0, 10).split("-"); return s.length === 3 ? Number(s[1]) + "/" + Number(s[2]) : String(d); };

export function targetLabel(promo, { categories = [], brands = [], products = [] } = {}) {
  const ids = (promo.target_ids || []).map(String);
  const nameOf = (list, id) => { const x = list.find(r => String(r.id) === id); return x ? x.name : id; };
  switch (promo.target || "all") {
    case "all": return zhEn("全店", "Storewide");
    case "category": return listNames(ids.map(id => nameOf(categories, id)), zhEn("類", "categories"));
    case "brand": return listNames(ids.map(id => nameOf(brands, id)), zhEn("個品牌", "brands"));
    case "product": return listNames(ids.map(id => nameOf(products, id)), zhEn("件商品", "products"));
    case "tag": return listNames(ids.map(t => "#" + t), zhEn("個標籤", "tags"));
    default: return "";
  }
}

function bogoLabel(pr) {
  const buy = Math.floor(num(pr.buyQty)), free = Math.floor(num(pr.freeQty));
  const pct = pr.percent == null || pr.percent === "" ? 100 : num(pr.percent);
  if (pct >= 100) return zhEn("買 " + buy + " 送 " + free, "Buy " + buy + " get " + free + " free");
  if (buy === 1 && free === 1) return zhEn("第二件" + offLabel(pct), "2nd item " + offLabel(pct));
  return zhEn("買 " + buy + " 件，另 " + free + " 件" + offLabel(pct), "Buy " + buy + ", next " + free + " " + offLabel(pct));
}

export function describePromotion(promo, { categories = [], brands = [], products = [], sym = "$" } = {}) {
  if (!promo) return "";
  const pr = params(promo);
  const $ = (n) => sym + round2(num(n)).toLocaleString("en-US");
  const tgt = targetLabel(promo, { categories, brands, products });
  let main = "";
  switch (promo.type) {
    case "percent": main = tgt + " " + offLabel(pr.percent); break;
    // mixGroup: one amount off the matching items together; else per cart line
    case "amount": main = zhEn(tgt + (pr.mixGroup ? " 減 " : " 每項減 ") + $(pr.amount), tgt + " " + $(pr.amount) + (pr.mixGroup ? " off" : " off each")); break;
    case "fixed_price": main = zhEn(tgt + " 特價 " + $(pr.price), tgt + " at " + $(pr.price)); break;
    case "bogo": main = bogoLabel(pr) + zhEn("（", " (") + tgt + zhEn("）", ")"); break;
    case "bundle": main = zhEn(tgt + " 買 " + Math.max(1, num(pr.buyQty)) + " 件 " + offLabel(pr.percent), tgt + ": buy " + Math.max(1, num(pr.buyQty)) + " for " + offLabel(pr.percent)); break;
    case "tiered": {
      const ts = (Array.isArray(pr.tiers) ? pr.tiers : []).filter(t => num(t.min) > 0).sort((a, b) => num(a.min) - num(b.min));
      main = tgt + " " + (ts.length ? ts.map(t => num(t.min) + zhEn(" 件 ", " items ") + (t.percent != null && t.percent !== "" ? offLabel(t.percent) : zhEn("減 ", "minus ") + $(t.amount))).join(" / ") : zhEn("（未設階梯）", "(no tiers set)"));
      break;
    }
    default: main = tgt;
  }
  const parts = [main];
  if (pr.mixGroup && ["bogo", "bundle", "tiered"].includes(promo.type)) parts.push(zhEn("可混款", "Mix & match"));
  if (num(promo.min_spend) > 0) parts.push(zhEn("滿 ", "min. spend ") + $(promo.min_spend));
  const days = (promo.days_of_week || []).map(Number).filter(d => d >= 0 && d <= 6).sort();
  if (days.length && days.length < 7) parts.push(days.join() === "1,2,3,4,5" ? zhEn("平日", "Weekdays") : days.join() === "0,6" ? zhEn("每週六日", "Weekends") : zhEn("逢星期", "Every ") + days.map(d => DOW[d]).join(zhEn("", ", ")));
  if (promo.starts_on || promo.ends_on) parts.push(promo.starts_on && promo.ends_on ? md(promo.starts_on) + "–" + md(promo.ends_on) : promo.starts_on ? md(promo.starts_on) + zhEn(" 起", " onwards") : zhEn("至 ", "until ") + md(promo.ends_on));
  if (promo.code_required && promo.code) parts.push(zhEn("用碼 ", "code ") + String(promo.code).toUpperCase());
  if (promo.exclude_on_sale) parts.push(zhEn("特價品除外", "Excludes sale items"));
  if (promo.exclusive) parts.push(zhEn("獨享", "Exclusive"));
  if (promo.max_uses != null && promo.max_uses !== "") parts.push(zhEn("限 ", "limit ") + num(promo.max_uses) + zhEn(" 次", " times"));
  return parts.join(" · ");
}
